import React, { useCallback, useEffect, useRef, useState } from "react"

import { focusElement } from "utils/focusElement"

import Icon from "icons"
import ContentfulButton from "components/page-components/button"
import useScroll from "utils/useScroll"
import { useMedia } from "react-use"
import AccountMenu from "./account-menu"
import Search from "./search"
import {
  NavSection,
  MainNav,
  TopRow,
  Left,
  CloseIconButton,
  CloseIcon,
  Logo,
  Right,
  DesktopWrapper,
  AccountSection,
  SearchModalButton,
  MobileWrapper,
  Separator,
  BottomRow,
  MobileTabs,
  Tab,
  ArrowIcon,
  DesktopMenu,
  Items,
  Item,
  StyledGrid,
  MobileMenu,
  MobileGrids,
  TitleRow,
  Title,
  ScrollArrow,
  ScrollContainer,
  ScrollableGrid,
  MobileCard,
  MenuOverlay,
} from "./styles"

const SCROLL_THRESHOLD = 100
const DESKTOP_SCROLL_THRESHOLD = 200

const Navigation = ({
  tabs,
  variant = null,
  beforeNavElement,
  subNavElement,
  mobileAnnouncement,
  mainNavRef,
  hideNav,
  hideNavOnMobile,
  hideMainNav,
  backgroundColor = "",
  fontColor = "",
  isTranslucent = false,
  ...props
}) => {
  const getTitle = (tab) => tab.heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
  const findTabByTitle = (title) =>
    tabs.find((tab) => getTitle(tab).toLowerCase() === title.toLowerCase())
  const [menuOpen, setMenuOpen] = useState(false)
  const { direction, scrollY } = useScroll()
  const [hasInteracted, setHasInteracted] = useState(false)
  const navRef = useRef(null)
  const isDesktop = useMedia("(min-width: 1024px)")

  const [activeTab, setActiveTab] = useState(() => {
    if (!tabs || tabs.length === 0) {
      return null
    }

    let tab

    switch (variant) {
      case "institute":
      case "classes":
        tab = findTabByTitle("learn")
        break
      case "supplements":
        tab = findTabByTitle("shop")
        break
      case "podcasts":
        tab = findTabByTitle("listen")
        break
      default:
        tab = findTabByTitle("read")
    }
    // if no tab is found, default to the first tab
    return tab || tabs[0]
  })

  const [previousActiveTab, setPreviousActiveTab] = useState(null)
  const [selectedTab, setSelectedTab] = useState(null)
  const [selectedItem, setSelectedItem] = useState(tabs[0].items[0])
  const [menuOpenScrollPosition, setMenuOpenScrollPosition] = useState(0)

  const handleTabClick = (tab) => {
    if (!hasInteracted) setHasInteracted(true)
    if (menuOpen && selectedTab?.id === tab.id) {
      handleMenuClose()
      return
    }

    if (activeTab) {
      setPreviousActiveTab(activeTab)
      setActiveTab(null)
    }
    setSelectedTab(tab)
    setSelectedItem(tab.items[0])
    setMenuOpen(true)
    setMenuOpenScrollPosition(window.scrollY)
  }

  const handleArrowClick = () => {
    if (!menuOpen) {
      setPreviousActiveTab(activeTab)
      setSelectedTab(activeTab)
      setActiveTab(null)
      setSelectedItem(activeTab.items[0])
      setMenuOpen(true)
    } else {
      handleMenuClose()
    }
  }

  const handleItemClick = (item) => {
    setSelectedItem(item)
  }

  const handleMenuClose = useCallback(() => {
    if (menuOpen) {
      setSelectedTab(null)
      setMenuOpen(false)
      setMenuOpenScrollPosition(0) // Reset scroll position tracking
      setActiveTab(previousActiveTab)
    }
  }, [menuOpen, previousActiveTab])

  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    // get the width before hiding the scrollbar
    const oldWidth = document.documentElement.clientWidth

    // hide the scrollbar
    document.body.style.overflow = menuOpen && !isDesktop ? "hidden" : "unset"

    // get the new width after hiding the scrollbar
    const newWidth = document.documentElement.clientWidth

    // set the margin equal to the width of the scrollbar
    const scrollbarWidth = Math.max(0, newWidth - oldWidth)
    document.body.style.marginRight = `${scrollbarWidth}px`

    const onKeyDown = (event) => {
      if (
        menuOpen &&
        event.key === "Tab" &&
        event.target.dataset.nextfocusselector &&
        event.shiftKey === false
      ) {
        focusElement(event, event.target.dataset.nextfocusselector)
      }
    }

    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [menuOpen, isDesktop])

  useEffect(() => {
    if (
      isDesktop &&
      menuOpen &&
      direction === "down" &&
      scrollY > menuOpenScrollPosition + DESKTOP_SCROLL_THRESHOLD
    ) {
      handleMenuClose()
    }
  }, [direction, scrollY, menuOpen, menuOpenScrollPosition, handleMenuClose, isDesktop])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && isDesktop && navRef.current && !navRef.current.contains(event.target)) {
        handleMenuClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [menuOpen, handleMenuClose, isDesktop])

  const logoLinkMap = {
    supplements: {
      href: "https://shop.mindbodygreen.com",
      title: "the mindbodygreen shop",
    },
    classes: {
      href: "/classes",
      title: "classes",
    },
    checkout: {
      href: "/classes",
      title: "classes",
    },
  }
  const getScrollTop = () => {
    if (typeof window === "undefined") return 0
    return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
  }
  const logoLink =
    variant && variant in logoLinkMap ? logoLinkMap[variant] : { href: "/", title: "home" }

  const shouldHideOnScroll = direction === "down" && getScrollTop() > SCROLL_THRESHOLD && !menuOpen

  return (
    <>
      <NavSection
        hide={hideNav}
        hideOnMobile={hideNavOnMobile}
        {...props}
        $hideNavOnScroll={shouldHideOnScroll}
        $menuOpen={menuOpen}
        ref={navRef}
      >
        {beforeNavElement}
        <MainNav
          ref={mainNavRef}
          variant={variant}
          backgroundColor={backgroundColor}
          isTranslucent={isTranslucent}
          hideMainNav={hideMainNav}
        >
          <TopRow>
            <Left>
              {variant !== "checkout" && (
                <CloseIconButton
                  onClick={handleMenuClose}
                  title="Close menu"
                  aria-label="Close menu"
                  hide={!menuOpen}
                >
                  <CloseIcon id="close" width={35} height={35} />
                </CloseIconButton>
              )}
              <Logo
                href={logoLink.href}
                title={logoLink.title}
                fontColor={fontColor}
                isTranslucent={isTranslucent}
                hide={menuOpen}
              />
            </Left>
            {variant !== "checkout" && (
              <Right>
                <DesktopWrapper>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      active={tab.id === activeTab?.id}
                      selected={tab.id === selectedTab?.id}
                      onClick={() => handleTabClick(tab)}
                      title={getTitle(tab)}
                      aria-label={getTitle(tab)}
                    >
                      {getTitle(tab)}
                    </Tab>
                  ))}
                </DesktopWrapper>
                {!["astro", "classes"].includes(variant) && (
                  <AccountSection>
                    <AccountMenu variant={variant} />
                    <SearchModalButton
                      id="search"
                      type="button"
                      onClick={() => setSearchOpen(true)}
                    >
                      <Icon id="search-icon" height={23} title="Search" />
                    </SearchModalButton>
                  </AccountSection>
                )}
              </Right>
            )}
          </TopRow>
          {variant !== "checkout" && (
            <MobileWrapper>
              <Separator />
              <BottomRow>
                <MobileTabs>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      active={tab.id === activeTab?.id}
                      selected={tab.id === selectedTab?.id}
                      onClick={() => handleTabClick(tab)}
                      title={getTitle(tab)}
                      aria-label={getTitle(tab)}
                    >
                      {getTitle(tab)}
                    </Tab>
                  ))}
                </MobileTabs>
                <ArrowIcon
                  id="arrow-navigation"
                  width={25}
                  height={16}
                  $menuOpen={menuOpen}
                  onClick={handleArrowClick}
                />
              </BottomRow>
            </MobileWrapper>
          )}
        </MainNav>
        {subNavElement}
        <>
          <MenuOverlay
            $menuOpen={hasInteracted && menuOpen}
            onClick={() => {
              if (menuOpen && isDesktop) handleMenuClose()
            }}
          />
          <DesktopMenu
            aria-expanded
            aria-controls="nav-menu-container"
            $menuOpen={hasInteracted && menuOpen}
          >
            <Items>
              {selectedTab?.items.map((item) => (
                <Item
                  key={item.id}
                  selected={item.id === selectedItem.id}
                  onClick={() => handleItemClick(item)}
                >
                  {getTitle(item)}
                </Item>
              ))}
            </Items>
            <StyledGrid component={selectedItem} />
          </DesktopMenu>
          {menuOpen && (
            <MobileMenu aria-expanded aria-controls="nav-menu-container">
              <MobileGrids>
                {selectedTab.items.map((item) => (
                  <React.Fragment key={item.id}>
                    <TitleRow>
                      <Title>{getTitle(item)}</Title>
                      {item.button && <ContentfulButton component={item.button} />}
                    </TitleRow>
                    <ScrollArrow />
                    <ScrollContainer>
                      <ScrollableGrid>
                        {item.items.map((card) => (
                          <MobileCard
                            key={card.id}
                            component={card}
                            variant="4col"
                            headlineVariant="medium"
                            isGrid
                          />
                        ))}
                      </ScrollableGrid>
                    </ScrollContainer>
                  </React.Fragment>
                ))}
              </MobileGrids>
            </MobileMenu>
          )}
        </>
      </NavSection>
      {searchOpen && <Search onClose={() => setSearchOpen(false)} />}
      {mobileAnnouncement}
    </>
  )
}

export default Navigation
